import { useMemo } from "react";
import { amp } from "src/api/amplitude";
import PhotoVariant from "src/api/graphql/__generated__/enums/PhotoVariant";
import OpacityView from "src/components/OpacityView";
import { SharePayloadProp } from "src/components/ShareMenu/types";
import parseV0TemplateUrl from "src/helpers/parseV0TemplateUrl";
import useStyle from "src/hooks/useStyle";
import useImageGalleryDimensions from "src/screens/(internal)/PhotoDetailModal/hooks/useImageGalleryDimensions";
import { color } from "src/styles";
import { useSafeArea } from "src/swsh-native";
import useIsSettledPhotoCaughtUp from "../../../../hooks/useIsSettledPhotoCaughtUp";
import usePhotoDetailShareLink from "../../../../hooks/usePhotoDetailShareLink";
import usePhotoDetailStore from "../../../../stores/usePhotoDetailStore";
import PhotoDetailFooterContent from "./components/PhotoDetailFooterContent";

const PhotoDetailFooter = () => {
	const debouncedSettledPhoto = usePhotoDetailStore((state) => state.debouncedSettledPhoto);
	const isSettledPhotoCaughtUp = useIsSettledPhotoCaughtUp();
	const shouldAttemptLoad = usePhotoDetailStore(
		// Using initial photo here because we want to avoid the flashes that come from using cur photo
		// Having this not show up until the initial photo is loaded is fine
		(state) => !state.isWaitingForInitialPhotoLoad,
	);
	const { carouselItemDimensions } = useImageGalleryDimensions();
	const { theme } = useStyle();
	const safeArea = useSafeArea();

	const imageShareProps = useMemo<SharePayloadProp | null>(() => {
		if (!debouncedSettledPhoto) return null;
		const successAmplitude = {
			eventName: amp.photo.shareAction,
			eventProperties: {
				photoId: debouncedSettledPhoto.photoId,
				ownerId: debouncedSettledPhoto.ownerId,
				isTopPhoto: debouncedSettledPhoto.isTopPhoto,
				variant: debouncedSettledPhoto.variant,
			},
		};
		const imageUrl = parseV0TemplateUrl({
			readUrl: debouncedSettledPhoto.readUrl,
			direct: "original",
		});
		const videoUrl = parseV0TemplateUrl({
			readUrl: debouncedSettledPhoto.readUrl,
			direct: "original-video",
		});
		const optimizedImageUrl =
			parseV0TemplateUrl({
				readUrl: debouncedSettledPhoto.readUrl,
				aspectRatio: "1x1",
				size: "xl",
				strategy: "inside",
				format: "webp",
			}) ?? undefined;

		if (debouncedSettledPhoto.variant === PhotoVariant.LivePhoto && imageUrl && videoUrl) {
			return {
				data: [
					{
						type: "LivePhoto",
						url: imageUrl,
						optimizedUrl: optimizedImageUrl,
						videoUrl,
						successAmplitude,
					},
				],
			} satisfies SharePayloadProp;
		} else if (debouncedSettledPhoto.variant === PhotoVariant.Video && videoUrl) {
			return {
				data: [
					{
						type: "Video",
						videoUrl,
						successAmplitude,
					},
				],
			};
		} else {
			if (!imageUrl) return null;
			return {
				data: [
					{
						type: "Image",
						url: imageUrl,
						optimizedUrl: optimizedImageUrl,
						successAmplitude,
					},
				],
			} satisfies SharePayloadProp;
		}
	}, [debouncedSettledPhoto]);
	const { linkShareProps } = usePhotoDetailShareLink();

	if (!imageShareProps && !linkShareProps) return null;
	if (!shouldAttemptLoad) return null;
	return (
		<OpacityView
			pointerEvents={isSettledPhotoCaughtUp ? "box-none" : "none"}
			style={{
				position: "absolute",
				bottom: safeArea.bottom,
				width: "100%",
				height: carouselItemDimensions.height,
				marginVertical: carouselItemDimensions.paddingVertical,
				backgroundColor: color.Background[theme],
				alignItems: "center",
				justifyContent: "center",
			}}
		>
			<PhotoDetailFooterContent
				photo={debouncedSettledPhoto}
				imageShareProps={imageShareProps}
				linkShareProps={linkShareProps}
			/>
		</OpacityView>
	);
};
export default PhotoDetailFooter;
